import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Download, Loader2, Receipt, PencilRuler, Phone } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { fileTypeIcon, fileTypeLabelKey } from "@/lib/fileTypes";

export default function OrderSuccess() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  // Accept legacy `order` param too (older confirmation emails sent that key).
  const orderId = params.get("order_id") || params.get("order");
  const mobile = params.get("mobile");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    setLoading(true);
    setFetchFailed(false);
    const url = mobile
      ? `/orders/${orderId}?mobile=${encodeURIComponent(mobile)}`
      : `/orders/${orderId}`;
    api.get(url)
      .then(({ data }) => setOrder(data))
      .catch(() => setFetchFailed(true))
      .finally(() => setLoading(false));
  }, [orderId, mobile, user]);

  const downloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const url = mobile
        ? `/orders/${orderId}/invoice?mobile=${encodeURIComponent(mobile)}`
        : `/orders/${orderId}/invoice`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice_${order?.order_number || orderId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setErrors(e => ({ ...e, _invoice: formatApiErrorDetail(err.response?.data?.detail) || err.message }));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const downloadItem = async (it) => {
    const k = `${it.plan_id}::${it.file_type}`;
    setDownloading(k);
    setErrors(e => ({ ...e, [k]: null }));
    try {
      const res = await api.post("/downloads", {
        plan_id: it.plan_id, file_type: it.file_type,
        name: order?.billing_name || user?.name || "Customer",
        mobile: mobile || order?.billing_mobile || user?.mobile || "",
        email: order?.billing_email || user?.email || null,
        device_fingerprint: navigator.userAgent.slice(0, 64),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(it.plan_title || "plan").replace(/\s+/g, "_")}_${it.file_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) { try { detail = JSON.parse(await err.response.data.text()).detail; } catch {} }
      setErrors(e => ({ ...e, [k]: formatApiErrorDetail(detail) || err.message }));
    } finally {
      setDownloading(null);
    }
  };

  const items = order?.items || [];
  const isPaid = order?.status === "paid";

  return (
    <div className="bg-slate-50/50 min-h-[60vh]" data-testid="order-success-page">
      <div className="nc-container py-12 max-w-2xl">
        <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("orderSuccess.title")}</h1>
          <p className="mt-1 text-sm text-slate-600">{t("orderSuccess.orderId")}: <span className="font-mono">{order?.order_number || orderId}</span></p>
          <p className="mt-3 text-sm text-slate-600">{t("orderSuccess.ready")}</p>
          {isPaid && (
            <button onClick={downloadInvoice} disabled={downloadingInvoice}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              data-testid="invoice-btn">
              {downloadingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
              Download tax invoice (PDF)
            </button>
          )}
          {errors._invoice && <p className="mt-2 text-xs text-rose-600">{errors._invoice}</p>}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {loading && items.length === 0 && (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              {user ? (
                fetchFailed ? (
                  <>We couldn't load this order's details right now. You can view it from <Link to="/dashboard" className="text-emerald-700 font-semibold">your dashboard</Link>.</>
                ) : (
                  <>This order has no downloadable items. View it in <Link to="/dashboard" className="text-emerald-700 font-semibold">your dashboard</Link>.</>
                )
              ) : (
                <>{t("orderSuccess.loginToDownload")} <Link to={`/login?next=/order/success?order_id=${orderId}${mobile ? `&mobile=${encodeURIComponent(mobile)}` : ""}`} className="text-emerald-700 font-semibold">{t("orderSuccess.login")}</Link></>
              )}
            </div>
          )}
          {items.map((it, idx) => {
            const isDesignTier = it.file_type === "design_tier";
            const Icon = isDesignTier ? PencilRuler : fileTypeIcon(it.file_type);
            const k = `${it.plan_id}::${it.file_type}`;
            return (
              <div key={k} className="p-4 flex items-center gap-3" data-testid={`success-item-${idx}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{it.plan_title}</p>
                  <p className="text-xs text-slate-500">
                    {isDesignTier
                      ? `Design package · ${it.validity_days || 30}-day modification validity`
                      : t(fileTypeLabelKey(it.file_type))}
                  </p>
                  {errors[k] && <p className="text-xs text-rose-600 mt-1">{errors[k]}</p>}
                </div>
                {isDesignTier ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
                        data-testid={`design-tier-callback-${idx}`}>
                    <Phone className="w-3.5 h-3.5" /> Our team will call you
                  </span>
                ) : (
                  <button onClick={() => downloadItem(it)} disabled={downloading === k}
                    className="nc-btn-primary text-sm py-2"
                    data-testid={`download-btn-${idx}`}>
                    {downloading === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {t("orderSuccess.download")}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-center">
          <Link to="/plans" className="text-sm font-semibold text-slate-600 hover:text-emerald-700">{t("orderSuccess.continueBrowse")}</Link>
        </div>
      </div>
    </div>
  );
}
